import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement[]} media
 * @property {HTMLElement} thumbnails
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = new Set();

  connectedCallback() {
    super.connectedCallback();

    this.refs.dialog.addEventListener('scroll', this.handleScroll);
    this.refs.dialog.addEventListener('wheel', this.handleImageWheel, {
      passive: false,
    });
    this.refs.dialog.addEventListener('close', this.handleZoomReset);
  }

  disconnectedCallback() {
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
    this.refs.dialog.removeEventListener('wheel', this.handleImageWheel);
    this.refs.dialog.removeEventListener('close', this.handleZoomReset);

    super.disconnectedCallback();
  }

  /* Reset images to their normal size */
    resetImageZoom() {
    for (const container of this.refs.media) {
      container.style.setProperty('--image-zoom', '1');
      container.style.removeProperty('--zoom-width');
      container.style.removeProperty('--zoom-max-height');
      delete container.dataset.zoomBaseWidth;
    }
  }

  handleZoomReset = () => {
    this.resetImageZoom();
  };

  /* Scroll up to zoom in; scroll down to zoom out */
    handleImageWheel = (event) => {
    const { dialog, media } = this.refs;

    if (!dialog.open || event.ctrlKey || !event.deltaY) return;
    if (!(event.target instanceof Element)) return;

    // Only capture scrolling directly over the image.
    const image = event.target.closest('img.product-media__image');
    if (!(image instanceof HTMLImageElement)) return;

    const container = image.closest('.product-media-container--image');
    if (!container || !media.includes(container)) return;

    const rect = image.getBoundingClientRect();

    if (
      event.clientX < rect.left ||
      event.clientX > rect.right ||
      event.clientY < rect.top ||
      event.clientY > rect.bottom
    ) {
      return;
    }

    if (!image.naturalWidth || !image.naturalHeight) return;

    event.preventDefault();

    // Remember the initial fitted width.
    if (!container.dataset.zoomBaseWidth) {
      container.dataset.zoomBaseWidth = String(rect.width);
    }

    const baseWidth = Number(container.dataset.zoomBaseWidth);
    if (!baseWidth) return;

    const current =
      Number(container.style.getPropertyValue('--image-zoom')) || 1;

    const unit =
      event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? 200 : 1;

    const delta = Math.max(-200, Math.min(200, event.deltaY * unit));

    // Keep the full image width accessible without sideways scrolling.
    const maximum = Math.max(
      1,
      Math.min(2.5, container.clientWidth / baseWidth)
    );

    const next = Math.max(
      1,
      Math.min(maximum, current * Math.exp(-delta * 0.002))
    );

    container.style.setProperty('--image-zoom', String(next));
    container.style.setProperty('--zoom-width', `${baseWidth * next}px`);
    container.style.setProperty('--zoom-max-height', 'none');

    if (next > 1) this.loadHighResolutionImage(container);
  };

  /* Open the popup with the image fitted to the screen */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];

    if (!targetImage) return;

    this.resetImageZoom();

    const targetThumbnail = thumbnails.children[index];

    const open = () => {
      if (!dialog.open) dialog.showModal();

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }
    };

    const sourceImage =
      event.target instanceof Element
        ? event.target.closest('li,slideshow-slide')
        : null;

    if (
      !supportsViewTransitions() ||
      isLowPowerDevice() ||
      !sourceImage
    ) {
      open();
      this.selectThumbnail(index, { behavior: 'instant' });
      return;
    }

    const itemTransitionName = 'gallery-item-open';

    sourceImage.style.setProperty(
      'view-transition-name',
      itemTransitionName
    );

    const focalPoint = sourceImage.dataset.focalPoint;

    if (focalPoint) {
      document.documentElement.style.setProperty(
        '--gallery-media-focal-point',
        focalPoint
      );
    }

    await startViewTransition(() => {
      open();

      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty(
        'view-transition-name',
        itemTransitionName
      );
    });

    document.documentElement.style.removeProperty(
      '--gallery-media-focal-point'
    );

    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /* Load a sharper image while retaining container zoom settings */
  loadHighResolutionImage(mediaContainer) {
    if (
      !mediaContainer?.classList.contains(
        'product-media-container--image'
      )
    ) {
      return false;
    }

    const image = mediaContainer.querySelector(
      'img.product-media__image'
    );

    if (!(image instanceof HTMLImageElement)) return false;

    const url = image.getAttribute('data_max_resolution');

    if (!url || this.#highResImagesLoaded.has(url)) return false;

    this.#highResImagesLoaded.add(url);
    preloadImage(url);

    const newImage = new Image();

    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', url);
    newImage.setAttribute('ref', 'image');

    newImage.onload = () => {
      image.replaceWith(newImage);
    };

    newImage.onerror = () => {
      this.#highResImagesLoaded.delete(url);
    };

    newImage.src = url;
  }

  /* Keep the active thumbnail in sync with the visible image */
  handleScroll = debounce(async () => {
    if (!this.isConnected || !this.refs.dialog.open) return;

    const { media, thumbnails } = this.refs;
    const mostVisibleElement = await getMostVisibleElement(media);

    if (
      !mostVisibleElement ||
      !this.isConnected ||
      !this.refs.dialog.open
    ) {
      return;
    }

    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach(
      (button, i) => {
        button.setAttribute('aria-selected', `${i === activeIndex}`);
      }
    );

    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /* Close the popup using the existing theme transition */
  async close() {
    const { dialog, media } = this.refs;

    this.resetImageZoom();

    if (!supportsViewTransitions() || isLowPowerDevice()) {
      return this.closeDialog();
    }

    const mostVisibleElement = await getMostVisibleElement(media);

    if (!mostVisibleElement) return this.closeDialog();

    const activeIndex = media.indexOf(mostVisibleElement);
    const itemTransitionName = 'gallery-item-close';
    const mediaGallery = this.closest('media-gallery');
    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive
      ? mediaGallery.slideshow?.slides?.[activeIndex]
      : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();

    const focalPoint = slide.dataset.focalPoint;

    if (focalPoint) {
      document.documentElement.style.setProperty(
        '--gallery-media-focal-point',
        focalPoint
      );
    }

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty(
      'view-transition-name',
      itemTransitionName
    );

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');

      slide.style.setProperty(
        'view-transition-name',
        itemTransitionName
      );

      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');

    document.documentElement.style.removeProperty(
      '--gallery-media-focal-point'
    );
  }

  closeDialog() {
    this.resetImageZoom();
    this.refs.dialog.close();

    window.dispatchEvent(new DialogCloseEvent());
  }

  /* Escape closes the popup */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /* Switch images using the thumbnails */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';

    this.selectThumbnail(index, { behavior });
  }

  async handleThumbnailPointerEnter(index) {
    if (this.refs.media[index]) {
      this.loadHighResolutionImage(this.refs.media[index]);
    }
  }

  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    const { media, thumbnails } = this.refs;

    if (
      !thumbnails?.children.length ||
      isNaN(index) ||
      index < 0 ||
      index >= thumbnails.children.length
    ) {
      return;
    }

    const targetThumbnail = thumbnails.children[index];

    if (!(targetThumbnail instanceof HTMLElement)) return;

    this.resetImageZoom();

    Array.from(thumbnails.querySelectorAll('button')).forEach(
      (button, i) => {
        button.setAttribute('aria-selected', `${i === index}`);
      }
    );

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });

      this.loadHighResolutionImage(targetImage);
    }

    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/* Identify the most visible media container */
function getMostVisibleElement(elements) {
  if (!elements.length) return Promise.resolve(null);

  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio
            ? current
            : prev
        );

        observer.disconnect();
        resolve(mostVisible.target);
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}